import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { HealthLike } from "./environment-notice";

/**
 * The one `/health` poll, shared by ApiStatusBanner and EnvironmentBanner.
 *
 * Both already keyed the same cache entry — deliberately, so they'd share one
 * response — but each declared its OWN queryFn and retry policy. React Query
 * dedupes by key, so which policy actually ran depended on which component
 * mounted first. One definition removes that coin-flip.
 */

/** What the server reports beyond the sandbox marker. */
export interface HealthPayload extends HealthLike {
    schema_ok?: boolean;
    pending_migrations?: string[];
}

export const HEALTH_QUERY_KEY = ["health", "reachability"] as const;

/**
 * A DB-free endpoint answers in well under a second on a good link; this is
 * sized for a bad one. That it is bounded AT ALL is the point — the previous
 * bare `fetch("/health")` had no ceiling, so a stalled probe could sit pending
 * indefinitely and never recover to success.
 */
export const HEALTH_TIMEOUT_MS = 8_000;

/**
 * Spread the three attempts across ~7s (1s, 2s, 4s) instead of cramming them
 * into ~2s at a flat 1s apart. A single tower hand-off on two bars swallows a
 * tight burst whole — which is how the banner ended up flapping red at a tech
 * whose connection was working (#1360).
 */
export function healthRetryDelay(attempt: number): number {
    return Math.min(1_000 * 2 ** attempt, 8_000);
}

/**
 * How many CONSECUTIVE failed rounds before we're willing to tell a human the
 * server is unreachable. `connectivity.ts` learned this rule the expensive way
 * — it demands two failed probes before it believes the link is down — and the
 * API check is now held to the same standard.
 */
export const FAILED_ROUNDS_BEFORE_ALARM = 2;

export function useHealthQuery(opts?: { enabled?: boolean }): UseQueryResult<HealthPayload, Error> {
    return useQuery<HealthPayload, Error>({
        queryKey: HEALTH_QUERY_KEY,
        queryFn: async () => {
            const res = await fetch("/health", { signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS) });
            if (!res.ok) throw new Error(`${res.status}`);
            return (await res.json()) as HealthPayload;
        },
        staleTime: 15_000,
        refetchInterval: 45_000,
        retry: 2,
        retryDelay: healthRetryDelay,
        enabled: opts?.enabled ?? true,
    });
}
